import styled, { css, keyframes } from 'styled-components';
import {
  black,
  blue,
  border1,
  exactWhite,
  gray,
  green,
  lightGray,
} from 'utils/style/colors';
import { zindex } from 'utils/style/zindex';
import { fadeIn, fadeOut } from 'utils/style/animations';

const transitionName = 'analyse-ai-drawer';

const fadeInAnimation = css`
  ${fadeIn} 0.3s ease;
`;

const fadeOutAnimation = css`
  ${fadeOut} 0.3s ease;
`;

export const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: ${zindex.modal};
  display: flex;
  justify-content: flex-end;
  border-radius: 15px;

  &.${transitionName}-enter {
    animation: ${fadeInAnimation};
  }

  &.${transitionName}-exit {
    animation: ${fadeOutAnimation};
  }
`;

export const DrawerPanel = styled.aside`
  position: relative;
  height: 100%;
  width: min(28rem, 100vw);
  max-width: 100%;
  background-color: ${exactWhite};
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 15px;

  .${transitionName}-enter-active &,
  .${transitionName}-enter-done & {
    transform: translateX(0);
  }

  .${transitionName}-exit & {
    transform: translateX(0);
  }

  .${transitionName}-exit-active & {
    transform: translateX(100%);
  }
`;

export const DrawerHeader = styled.header`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${lightGray};
  flex-shrink: 0;
`;

export const DrawerMainHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const DrawerTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const ChatMessages = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ChatEmptyState = styled.div`
  margin: auto;
  max-width: 18rem;
  text-align: center;
  color: ${gray};
  font-size: 0.95rem;
  line-height: 1.6;
  padding: 1rem 0.5rem;
`;

export const ChatMessageGroup = styled.div<{ isUser?: boolean }>`
  align-self: ${(props) => (props.isUser ? 'flex-start' : 'flex-end')};
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isUser ? 'flex-start' : 'flex-end')};
  gap: 0.35rem;
  max-width: 85%;
`;

export const ChatBubble = styled.div<{ isUser?: boolean }>`
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.5;
  word-break: break-word;
  background: ${(props) => (props.isUser ? lightGray : blue)};
  color: ${(props) => (props.isUser ? black : exactWhite)};
`;

export const MarkdownBubbleContent = styled.div`
  & * {
    color: ${exactWhite} !important;
  }

  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  & p {
    margin: 0 0 0.5rem;
  }

  & ul,
  & ol {
    margin: 0.25rem 0 0.5rem;
    padding-inline-start: 1.25rem;
  }

  & li {
    margin-bottom: 0.25rem;
  }

  & strong {
    font-weight: 600;
  }

  & a {
    color: inherit;
    text-decoration: underline;
    font-weight: 600;
  }

  & code {
    background: rgba(255, 255, 255, 0.18) !important;
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
  }

  & pre {
    margin: 0.35rem 0 0.5rem;
    background: rgba(0, 0, 0, 0.18) !important;
    padding: 0.5rem;
    border-radius: 0.5rem;
    overflow-x: auto;

    & code {
      background: none;
      padding: 0;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4 {
    margin: 0.5rem 0 0.35rem;
    font-size: 1em;
    font-weight: 600;
  }

  & blockquote {
    margin: 0.35rem 0;
    padding-inline-start: 0.75rem;
    border-inline-start: 3px solid rgba(255, 255, 255, 0.35);
  }

  & table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.35rem 0 0.5rem;
    font-size: 0.875em;
  }

  & th,
  & td {
    border: 1px solid rgba(255, 255, 255, 0.25);
    padding: 0.25rem 0.4rem;
    text-align: start;
  }
`;

export const ResponseTime = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: ${black};
  padding: 0 0.15rem;
`;

export const ResponseTimeValue = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${green};
`;

const dotBounce = keyframes`
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

export const LoadingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;

  & span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${exactWhite};
    animation: ${dotBounce} 1.2s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

export const ChatInputArea = styled.div`
  flex-shrink: 0;
  padding: 0.85rem 1.25rem 1.25rem;
  border-top: 1px solid ${lightGray};
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const ChatTextarea = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  max-height: 10rem;
  resize: vertical;
  border: ${border1};
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${black};
  background-color: ${exactWhite};
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus {
    border-color: ${blue};
    box-shadow: 0 0 0 2px rgba(64, 149, 209, 0.2);
  }

  &::placeholder {
    color: ${gray};
    font-weight: normal;
  }

  &:disabled {
    background-color: ${lightGray};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const SendButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const drawerTransitionName = transitionName;
